import { useState, useMemo, useCallback, useEffect } from "react";
import { Search, ShieldCheck, Info, Database, Calculator, Plus, Trash2, ShoppingCart, AlertTriangle, TrendingDown, Phone } from "lucide-react";
import { cotizadorData, COTIZADOR_META } from "@/mocks/cotizador_data";
import { formatCLP, formatNum } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { trackCotizadorQuery } from "@/lib/analytics";

// ISAPREs principales
const ISAPRES = [
  "FONASA",
  "COLMENA GOLDEN CROSS S.A.",
  "Isapre Cruz Blanca S.A.",
  "ISAPRE BANMEDICA S.A.",
  "ISAPRE CONSALUD S.A.",
  "NUEVA MASVIDA S.A.",
  "ISAPRE VIDA TRES  S.A.",
  "ESENCIAL S.A.",
  "ISALUD ISAPRE DE CODELCO LIMITADA",
];

const AMBITOS = [...new Set(cotizadorData.map((d) => d.categoria))].sort();

function nombreCorto(nombre) {
  const map = {
    "COLMENA GOLDEN CROSS S.A.": "Colmena",
    "Isapre Cruz Blanca S.A.": "Cruz Blanca",
    "ISAPRE BANMEDICA S.A.": "Banmédica",
    "ISAPRE CONSALUD S.A.": "Consalud",
    "NUEVA MASVIDA S.A.": "Nueva Masvida",
    "ISAPRE VIDA TRES  S.A.": "Vida Tres",
    "ESENCIAL S.A.": "Esencial",
    "ISALUD ISAPRE DE CODELCO LIMITADA": "Isalud (Codelco)",
  };
  return map[nombre] || nombre;
}

function codigoFonasa(cod) {
  const limpio = cod.replace(/^0+/, '');
  return limpio.length < 4 ? cod : limpio;
}

// Categorías que permiten carrito
const CATEGORIAS_CARRITO = ["Laboratorio", "Imagenologia"];

// Placeholders contextuales por categoría
const PLACEHOLDERS = {
  Laboratorio: "Ej: hemograma, perfil bioquímico, TSH, creatinina...",
  Consultas: "Ej: oftalmología, dermatología, cardiología...",
  Imagenologia: "Ej: ecografía abdominal, resonancia rodilla...",
  "Kinesiologia y Rehabilitacion": "Ej: sesión kinesiología, terapia ocupacional...",
  Procedimientos: "Ej: endoscopía, biopsia, electrocardiograma...",
};

// Indicador de confianza
function nivelConfianza(n) {
  if (n >= 1000) return { label: "Alta confianza", color: "bg-emerald-100 text-emerald-700" };
  if (n >= 100) return { label: "Confianza media", color: "bg-amber-100 text-amber-700" };
  return { label: "Muestra pequeña", color: "bg-red-100 text-red-700" };
}

// Prestaciones populares por categoría
function getPopulares(isapre, seguro, categoria) {
  const filtered = cotizadorData.filter(
    (d) => d.isapre === isapre && d.tiene_seguro === seguro && (!categoria || d.categoria === categoria)
  );
  const map = {};
  for (const d of filtered) {
    if (!map[d.cod]) map[d.cod] = { cod: d.cod, nombre: d.nombre, n: d.n_total, resultado: d };
    else map[d.cod].n += d.n_total;
  }
  return Object.values(map).sort((a, b) => b.n - a.n).slice(0, 6);
}

// Mapa de aliases cortos para ISAPREs en URLs
const ISAPRE_ALIASES = {
  "fonasa": "FONASA",
  "colmena": "COLMENA GOLDEN CROSS S.A.",
  "cruzblanca": "Isapre Cruz Blanca S.A.",
  "cruz_blanca": "Isapre Cruz Blanca S.A.",
  "banmedica": "ISAPRE BANMEDICA S.A.",
  "consalud": "ISAPRE CONSALUD S.A.",
  "masvida": "NUEVA MASVIDA S.A.",
  "vidatres": "ISAPRE VIDA TRES  S.A.",
  "vida_tres": "ISAPRE VIDA TRES  S.A.",
  "esencial": "ESENCIAL S.A.",
  "isalud": "ISALUD ISAPRE DE CODELCO LIMITADA",
  "codelco": "ISALUD ISAPRE DE CODELCO LIMITADA",
};

function resolveIsapre(param) {
  if (!param) return "";
  // Intento directo (nombre completo URL-encoded)
  const decoded = decodeURIComponent(param);
  if (ISAPRES.includes(decoded)) return decoded;
  // Intento por alias
  const alias = ISAPRE_ALIASES[decoded.toLowerCase()];
  if (alias) return alias;
  // Intento parcial (contiene)
  const found = ISAPRES.find((i) => i.toLowerCase().includes(decoded.toLowerCase()));
  return found || "";
}

export default function Cotizador() {
  const { user } = useAuth();
  const [ambito, setAmbito] = useState("");
  const [isapre, setIsapre] = useState("");
  const [seguroComp, setSeguroComp] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [canasta, setCanasta] = useState([]);
  const [fromLink, setFromLink] = useState(false);

  // Leer query params al montar (deep link)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pParam = params.get("prestaciones") || params.get("p");
    const iParam = params.get("isapre") || params.get("i");
    const sParam = params.get("seguro") || params.get("s");

    if (!pParam) return; // No hay deep link

    // Resolver ISAPRE
    const resolvedIsapre = resolveIsapre(iParam);
    if (resolvedIsapre) setIsapre(resolvedIsapre);

    // Resolver seguro
    if (sParam === "1" || sParam === "true" || sParam === "si") {
      setSeguroComp(true);
    }

    // Resolver prestaciones por código
    const codigos = pParam.split(",").map((c) => c.trim());
    const seguro = sParam === "1" || sParam === "true" || sParam === "si";
    const isapreToUse = resolvedIsapre || "FONASA"; // fallback

    const items = [];
    for (const cod of codigos) {
      // Buscar en cotizadorData con la ISAPRE y seguro indicados
      let match = cotizadorData.find(
        (d) => d.cod === cod && d.isapre === isapreToUse && d.tiene_seguro === seguro
      );
      // Si no hay match exacto, buscar sin seguro
      if (!match) {
        match = cotizadorData.find(
          (d) => d.cod === cod && d.isapre === isapreToUse
        );
      }
      // Si no hay con esa ISAPRE, buscar con cualquiera (para mostrar nombre al menos)
      if (!match) {
        match = cotizadorData.find((d) => d.cod === cod);
      }
      if (match && !items.some((it) => it.cod === cod)) {
        items.push({ cod: match.cod, nombre: match.nombre, resultado: match });
      }
    }

    if (items.length > 0) {
      setCanasta(items);
      setFromLink(true);
    }

    // Limpiar URL sin recargar
    if (window.history.replaceState) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // Prestaciones filtradas
  const prestacionesDisponibles = useMemo(() => {
    if (!isapre) return [];
    let filtered = cotizadorData.filter(
      (d) => d.isapre === isapre && d.tiene_seguro === seguroComp
    );
    if (ambito) filtered = filtered.filter((d) => d.categoria === ambito);
    const map = {};
    for (const d of filtered) {
      if (!map[d.cod]) map[d.cod] = { cod: d.cod, nombre: d.nombre, n: d.n_total, resultado: d };
      else map[d.cod].n += d.n_total;
    }
    return Object.values(map).sort((a, b) => b.n - a.n);
  }, [isapre, seguroComp, ambito]);

  // Search filter
  const prestacionesFiltradas = useMemo(() => {
    if (!searchQuery) return prestacionesDisponibles.slice(0, 50);
    const q = searchQuery.toLowerCase();
    return prestacionesDisponibles
      .filter((p) => p.nombre.toLowerCase().includes(q) || p.cod.toLowerCase().includes(q) || codigoFonasa(p.cod).includes(q))
      .slice(0, 30);
  }, [prestacionesDisponibles, searchQuery]);

  // Populares
  const populares = useMemo(() => {
    if (!isapre) return [];
    return getPopulares(isapre, seguroComp, ambito);
  }, [isapre, seguroComp, ambito]);

  // Recalcular canasta cuando cambia seguroComp (sin borrar items)
  const canastaCalculada = useMemo(() => {
    return canasta.map((item) => {
      const nuevoResultado = cotizadorData.find(
        (d) => d.cod === item.cod && d.isapre === isapre && d.tiene_seguro === seguroComp
      );
      if (nuevoResultado) {
        return { ...item, resultado: nuevoResultado, sinDatos: false };
      }
      // Si no hay datos para esta condición, mantener el resultado anterior con aviso
      return { ...item, sinDatos: true };
    });
  }, [canasta, isapre, seguroComp]);

  // Totales canasta (usa canastaCalculada)
  const totalesCanasta = useMemo(() => {
    const validos = canastaCalculada.filter((item) => !item.sinDatos);
    if (validos.length === 0) return null;
    return {
      p10: validos.reduce((sum, item) => sum + (item.resultado.p10 || 0), 0),
      p25: validos.reduce((sum, item) => sum + (item.resultado.p25 || 0), 0),
      p50: validos.reduce((sum, item) => sum + (item.resultado.p50 || 0), 0),
      p75: validos.reduce((sum, item) => sum + (item.resultado.p75 || 0), 0),
      p90: validos.reduce((sum, item) => sum + (item.resultado.p90 || 0), 0),
      avg_precio: validos.reduce((sum, item) => sum + (item.resultado.avg_precio || 0), 0),
      avg_bonif: validos.reduce((sum, item) => sum + (item.resultado.avg_bonif || 0), 0),
      avg_seg_compl: validos.reduce((sum, item) => sum + (item.resultado.avg_seg_compl || 0), 0),
      n_prestaciones: validos.length,
      n_total: validos.reduce((sum, item) => sum + (item.resultado.n_total || 0), 0),
      sinDatosCount: canastaCalculada.filter((item) => item.sinDatos).length,
    };
  }, [canastaCalculada]);

  // Track
  useEffect(() => {
    if (canasta.length === 0) return;
    const last = canasta[canasta.length - 1];
    trackCotizadorQuery({
      ambito, prestacion_cod: last.cod, prestacion_nombre: last.nombre,
      isapre, seguro_comp: seguroComp, p50: last.resultado.p50,
      n_observaciones: last.resultado.n_total, pct_copago_0: last.resultado.pct_copago_0,
    });
  }, [canasta.length]);

  const agregarACanasta = useCallback((prestacion) => {
    if (canasta.some((item) => item.cod === prestacion.cod)) return;
    const resultado = cotizadorData.find(
      (d) => d.cod === prestacion.cod && d.isapre === isapre && d.tiene_seguro === seguroComp
    );
    if (!resultado) return;
    setCanasta((prev) => [...prev, { cod: prestacion.cod, nombre: prestacion.nombre, resultado }]);
  }, [canasta, isapre, seguroComp]);

  const quitarDeCanasta = useCallback((index) => {
    setCanasta((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const limpiar = () => {
    setAmbito(""); setIsapre(""); setSeguroComp(false);
    setSearchQuery(""); setSearchOpen(false); setCanasta([]);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3 sm:py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-slate-900">Cotizador de Copago</h1>
            <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5">
              Estima cuánto pagarás · Actualizado {COTIZADOR_META.generado}
            </p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <span className="text-xs text-slate-400 hidden sm:inline">{user?.username}</span>
            <button
              onClick={() => { localStorage.removeItem("uc_auth_session"); window.location.href = "/login"; }}
              className="text-xs text-slate-500 hover:text-slate-700 px-2 sm:px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50"
              aria-label="Cerrar sesión"
            >
              Salir
            </button>
          </div>
        </div>
      </header>

      {/* Disclaimer legal */}
      <div className="bg-amber-50 border-b border-amber-200 px-4 sm:px-6 py-2">
        <div className="max-w-6xl mx-auto flex items-start sm:items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5 sm:mt-0" />
          <p className="text-[10px] sm:text-[11px] text-amber-800">
            Monto referencial. No constituye presupuesto. El copago final puede variar según tu plan y condiciones de la atención.
          </p>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Panel izquierdo: filtros — en mobile va primero, en desktop a la izquierda */}
          <div className="lg:col-span-1 order-1">
            <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
              <h2 className="text-sm font-semibold text-slate-900">¿Qué necesitas cotizar?</h2>

              {/* Categoría */}
              <div className="space-y-1.5">
                <label htmlFor="cot-ambito" className="text-xs font-medium text-slate-600">Categoría</label>
                <select
                  id="cot-ambito"
                  value={ambito}
                  onChange={(e) => { setAmbito(e.target.value); setSearchQuery(""); setCanasta([]); }}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 bg-white"
                >
                  <option value="">Todas las categorías</option>
                  {AMBITOS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>

              {/* Previsión */}
              <div className="space-y-1.5">
                <label htmlFor="cot-isapre" className="text-xs font-medium text-slate-600">Tu previsión</label>
                <select
                  id="cot-isapre"
                  value={isapre}
                  onChange={(e) => { setIsapre(e.target.value); setSearchQuery(""); setCanasta([]); }}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 bg-white"
                >
                  <option value="">Seleccionar previsión...</option>
                  {ISAPRES.map((i) => <option key={i} value={i}>{nombreCorto(i)}</option>)}
                </select>
              </div>

              {/* Seguro complementario */}
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="cot-seguro"
                  checked={seguroComp}
                  onChange={(e) => setSeguroComp(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                />
                <label htmlFor="cot-seguro" className="text-xs text-slate-700 flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
                  Tengo seguro complementario
                </label>
              </div>

              {/* Búsqueda */}
              {isapre && (
                <div className="space-y-1.5">
                  <label htmlFor="cot-search" className="text-xs font-medium text-slate-600">Prestación</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      id="cot-search"
                      type="text"
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true); }}
                      onFocus={() => setSearchOpen(true)}
                      placeholder={PLACEHOLDERS[ambito] || "Buscar por nombre o código FONASA..."}
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400"
                    />
                    {searchOpen && prestacionesFiltradas.length > 0 && (
                      <div className="absolute z-50 top-full mt-1 w-full max-h-56 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg">
                        {prestacionesFiltradas.map((p) => {
                          const esCarrito = CATEGORIAS_CARRITO.includes(ambito);
                          return (
                            <button
                              key={p.cod}
                              type="button"
                              onClick={() => { agregarACanasta(p); if (!esCarrito) { setSearchQuery(p.nombre); } setSearchOpen(false); }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-violet-50 border-b border-slate-50 last:border-0"
                            >
                              <span className="font-medium text-slate-800 text-xs">{p.nombre}</span>
                              <span className="ml-2 text-[10px] text-slate-400">cód. {codigoFonasa(p.cod)} · {formatCLP(p.resultado.avg_precio)}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-400">
                    {formatNum(prestacionesDisponibles.length)} prestaciones disponibles
                    {CATEGORIAS_CARRITO.includes(ambito) && " · Agrega varias al carrito"}
                  </p>
                </div>
              )}

              {/* Prestaciones populares */}
              {isapre && !searchQuery && populares.length > 0 && canasta.length === 0 && (
                <div className="pt-2">
                  <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Más consultadas</p>
                  <div className="flex flex-wrap gap-1.5">
                    {populares.map((p) => (
                      <button
                        key={p.cod}
                        type="button"
                        onClick={() => agregarACanasta(p)}
                        className="px-2.5 py-1.5 rounded-lg text-[10px] font-medium text-violet-700 bg-violet-50 border border-violet-100 hover:bg-violet-100 transition truncate max-w-[180px]"
                      >
                        {p.nombre}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Limpiar */}
              <button
                type="button"
                onClick={limpiar}
                className="w-full mt-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50 transition"
              >
                Limpiar
              </button>
            </div>

            {/* Metadata — oculto en mobile cuando hay resultados, visible al final */}
            <div className={`mt-4 bg-white rounded-2xl border border-slate-200 p-5 ${canasta.length > 0 ? "hidden lg:block" : ""}`}>
              <div className="flex items-center gap-2 mb-3">
                <Database className="w-4 h-4 text-slate-400" />
                <h3 className="text-xs font-semibold text-slate-700">Sobre los datos</h3>
              </div>
              <div className="space-y-1.5 text-[11px] text-slate-500">
                <p><strong className="text-slate-700 tabular-nums">{formatNum(COTIZADOR_META.total_observaciones)}</strong> atenciones analizadas</p>
                <p><strong className="text-slate-700 tabular-nums">{formatNum(COTIZADOR_META.prestaciones_unicas)}</strong> prestaciones</p>
                <p>Período: <strong className="text-slate-700">{COTIZADOR_META.periodo}</strong></p>
                <p>Actualizado: <strong className="text-slate-700">{COTIZADOR_META.generado}</strong></p>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2">
                <Phone className="w-3 h-3 text-slate-400" />
                <p className="text-[10px] text-slate-500">¿No encuentras tu prestación? <span className="text-violet-600 font-medium">Llama al 2 2676 7000</span></p>
              </div>
            </div>
          </div>

          {/* Panel derecho: resultado — en mobile va arriba cuando hay resultados */}
          <div className={`lg:col-span-2 space-y-6 ${canasta.length > 0 ? "order-first lg:order-last" : "order-2"}`}>
            {/* Estado vacío */}
            {canasta.length === 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center text-white shadow-lg mb-4">
                    <Calculator className="w-8 h-8" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    {isapre ? "Busca tu prestación" : "¿Cuánto vas a pagar?"}
                  </h3>
                  <p className="text-sm text-slate-500 mt-2 max-w-md">
                    {isapre
                      ? "Escribe el nombre del examen, consulta o procedimiento que necesitas. Si vas a laboratorio, puedes agregar varios exámenes para ver el total."
                      : "Selecciona tu previsión de salud y te mostraremos una estimación basada en lo que pagaron otros pacientes en la misma situación."}
                  </p>
                  {!isapre && (
                    <div className="mt-4 p-3 rounded-xl bg-violet-50 border border-violet-100 max-w-sm">
                      <p className="text-[11px] text-violet-700">
                        <strong>¿Cómo funciona?</strong> Analizamos {formatNum(COTIZADOR_META.total_observaciones)} atenciones reales del último año para mostrarte el rango de copago más probable.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Canasta con resultados */}
            {canasta.length > 0 && totalesCanasta && (
              <>
                {/* Resumen principal */}
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <ShoppingCart className="w-4 h-4 text-violet-600" />
                      <h2 className="text-base font-semibold text-slate-900">Tu cotización</h2>
                      <span className="text-xs text-slate-500">({canasta.length} {canasta.length === 1 ? "prestación" : "prestaciones"})</span>
                    </div>
                    <button type="button" onClick={() => setCanasta([])} className="text-xs text-slate-500 hover:text-rose-600">
                      Vaciar
                    </button>
                  </div>

                  {/* Aviso si hay items sin datos para la condición actual */}
                  {totalesCanasta.sinDatosCount > 0 && (
                    <div className="mb-3 p-2.5 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                      <p className="text-[10px] text-amber-800">
                        {totalesCanasta.sinDatosCount} prestación(es) no tiene datos {seguroComp ? "con" : "sin"} seguro complementario. Se muestran con los datos disponibles.
                      </p>
                    </div>
                  )}

                  {/* Items */}
                  <div className="space-y-2 mb-4">
                    {canastaCalculada.map((item, i) => {
                      const conf = nivelConfianza(item.resultado.n_total);
                      return (
                        <div key={item.cod} className={`flex items-center justify-between p-3 rounded-xl border ${item.sinDatos ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-100"}`}>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-slate-800 truncate">{item.nombre}</div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-[10px] text-slate-500">cód. {codigoFonasa(item.cod)}</span>
                              {item.sinDatos && <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">Sin datos {seguroComp ? "con" : "sin"} seguro</span>}
                              {!item.sinDatos && <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${conf.color}`}>{conf.label}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 ml-3">
                            <div className="text-right">
                              <div className="text-[10px] text-slate-400 line-through tabular-nums">{formatCLP(item.resultado.avg_precio)}</div>
                              <div className="text-sm font-bold text-violet-700 tabular-nums">{formatCLP(item.resultado.p50)}</div>
                            </div>
                            <button
                              type="button"
                              onClick={() => quitarDeCanasta(i)}
                              className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-500 transition"
                              aria-label={`Quitar ${item.nombre}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Totales */}
                  <div className="border-t border-slate-200 pt-4">
                    <div className="text-center mb-4">
                      <div className="text-[10px] text-slate-500 uppercase font-semibold">Copago estimado</div>
                      <div className="text-3xl font-bold text-slate-900 tabular-nums mt-1">
                        {formatCLP(totalesCanasta.p10)} – {formatCLP(totalesCanasta.p90)}
                      </div>
                    </div>

                    <div className="p-4 rounded-xl bg-violet-50 border border-violet-100 mb-4">
                      <p className="text-sm text-violet-900 font-medium text-center">
                        La mayoría de los pacientes con {nombreCorto(isapre)} pagó entre <strong className="tabular-nums">{formatCLP(totalesCanasta.p10)}</strong> y <strong className="tabular-nums">{formatCLP(totalesCanasta.p90)}</strong>
                      </p>
                    </div>

                    {/* Comparador con/sin seguro */}
                    {seguroComp && totalesCanasta.avg_seg_compl > 0 && (
                      <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 mb-4 flex items-start gap-2">
                        <TrendingDown className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-xs text-emerald-800 font-medium">Tu seguro complementario te ahorra aprox. {formatCLP(totalesCanasta.avg_seg_compl)}</p>
                          <p className="text-[10px] text-emerald-600 mt-0.5">Sin seguro pagarías ~{formatCLP(totalesCanasta.p50 + totalesCanasta.avg_seg_compl)} · Con seguro pagas ~{formatCLP(totalesCanasta.p50)}</p>
                        </div>
                      </div>
                    )}

                    {/* Desglose */}
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Precio clínica</span>
                        <span className="font-semibold text-slate-900 tabular-nums">{formatCLP(totalesCanasta.avg_precio)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Bonificación {nombreCorto(isapre)}</span>
                        <span className="font-semibold text-blue-700 tabular-nums">-{formatCLP(totalesCanasta.avg_bonif)}</span>
                      </div>
                      {seguroComp && totalesCanasta.avg_seg_compl > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Seguro complementario</span>
                          <span className="font-semibold text-emerald-700 tabular-nums">-{formatCLP(totalesCanasta.avg_seg_compl)}</span>
                        </div>
                      )}
                      <div className="flex justify-between pt-2 border-t border-slate-200">
                        <span className="font-bold text-slate-900">Tu copago estimado</span>
                        <span className="font-bold text-violet-700 tabular-nums text-lg">{formatCLP(totalesCanasta.p50)}</span>
                      </div>
                    </div>

                    {/* Indicador de confianza global */}
                    {(() => {
                      const conf = nivelConfianza(totalesCanasta.n_total);
                      return (
                        <div className={`mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium ${conf.color}`}>
                          <Database className="w-3 h-3" /> {conf.label} · {formatNum(totalesCanasta.n_total)} observaciones
                        </div>
                      );
                    })()}

                    <div className="mt-3 flex items-start gap-1.5 text-[10px] text-slate-400">
                      <Info className="w-3 h-3 mt-0.5 shrink-0" />
                      <span>Monto referencial basado en atenciones reales. No constituye presupuesto.</span>
                    </div>
                  </div>
                </div>

                {/* Detalle por prestación — solo desktop (para analistas) */}
                <div className="hidden lg:block bg-white rounded-2xl border border-slate-200 p-6 space-y-6">
                  <h3 className="text-base font-semibold text-slate-900">Detalle por prestación</h3>
                  {canastaCalculada.filter((item) => !item.sinDatos).map((item) => {
                    const pctCopago = item.resultado.avg_precio > 0 ? (item.resultado.p50 / item.resultado.avg_precio * 100) : 0;
                    const conf = nivelConfianza(item.resultado.n_total);
                    return (
                      <div key={item.cod} className="border-b border-slate-100 pb-5 last:border-0 last:pb-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-slate-800">{item.nombre}</span>
                          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${conf.color}`}>{conf.label}</span>
                        </div>
                        <div className="text-[10px] text-slate-500 mb-3">
                          {formatNum(item.resultado.n_total)} atenciones analizadas · {item.resultado.pct_copago_0}% pagó $0
                        </div>

                        {/* Barra de percentiles */}
                        <div className="mb-3">
                          <div className="flex justify-between text-[9px] text-slate-400 mb-0.5">
                            <span>P10</span><span>P25</span><span>P50</span><span>P75</span><span>P90</span>
                          </div>
                          <div className="relative h-5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="absolute h-full bg-violet-200 rounded-full"
                              style={{ left: `${item.resultado.p90 > 0 ? (item.resultado.p10 / item.resultado.p90) * 100 : 0}%`, width: `${item.resultado.p90 > 0 ? ((item.resultado.p90 - item.resultado.p10) / item.resultado.p90) * 100 : 0}%` }} />
                            <div className="absolute h-full bg-violet-400 rounded-full"
                              style={{ left: `${item.resultado.p90 > 0 ? (item.resultado.p25 / item.resultado.p90) * 100 : 0}%`, width: `${item.resultado.p90 > 0 ? ((item.resultado.p75 - item.resultado.p25) / item.resultado.p90) * 100 : 0}%` }} />
                            <div className="absolute top-0 bottom-0 w-0.5 bg-violet-700"
                              style={{ left: `${item.resultado.p90 > 0 ? (item.resultado.p50 / item.resultado.p90) * 100 : 50}%` }} />
                          </div>
                          <div className="flex justify-between text-[10px] font-semibold text-slate-600 mt-0.5 tabular-nums">
                            <span>{formatCLP(item.resultado.p10)}</span>
                            <span>{formatCLP(item.resultado.p25)}</span>
                            <span className="text-violet-700">{formatCLP(item.resultado.p50)}</span>
                            <span>{formatCLP(item.resultado.p75)}</span>
                            <span>{formatCLP(item.resultado.p90)}</span>
                          </div>
                        </div>

                        {/* Desglose barras */}
                        <div className="space-y-1.5">
                          {[
                            { label: "Precio clínica", value: item.resultado.avg_precio, color: "bg-slate-300", pct: 100 },
                            { label: `Bonif. ${nombreCorto(isapre)}`, value: item.resultado.avg_bonif, color: "bg-blue-400", pct: item.resultado.avg_precio > 0 ? (item.resultado.avg_bonif / item.resultado.avg_precio * 100) : 0 },
                            ...(seguroComp && item.resultado.avg_seg_compl > 0 ? [{ label: "Seg. complementario", value: item.resultado.avg_seg_compl, color: "bg-emerald-400", pct: item.resultado.avg_precio > 0 ? (item.resultado.avg_seg_compl / item.resultado.avg_precio * 100) : 0 }] : []),
                            { label: "Copago (P50)", value: item.resultado.p50, color: "bg-violet-500", pct: pctCopago },
                          ].map((bar) => (
                            <div key={bar.label} className="flex items-center gap-2">
                              <div className="w-28 text-[10px] text-slate-600">{bar.label}</div>
                              <div className="flex-1 h-3.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className={`h-full ${bar.color} rounded-full`} style={{ width: `${Math.min(bar.pct, 100)}%` }} />
                              </div>
                              <div className="w-24 text-right text-[10px] font-semibold text-slate-700 tabular-nums">
                                {formatCLP(bar.value)} <span className="text-slate-400">({bar.pct.toFixed(0)}%)</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
